/**
 * OperationsPage — data-bound Operations tab (Plan 08 Task 8.3).
 *
 * Layout mirrors IA §5.1 (post-Phase-9: 7 widgets):
 *   Row 1 — KPI grid: Total cost | Avg latency | Error rate | Refusal rate
 *   Row 2 — Cost trend (2/3 width) | Latency distribution (1/3 width)
 *   Row 3 — Error breakdown table (2/3 width) | Complexity donut (1/3 width)
 *   Row 5 — Tool usage table (collapsible, default closed; Skills moved to
 *           dedicated /skills tab in Phase 9)
 *
 * The Jira prefill funnel lived here originally but was moved to the
 * Engagement tab 2026-04-24 — prefill adoption is an engagement signal
 * ("do users act on the Jira links inside their briefs") rather than an
 * ops health signal, so it now sits alongside DAU/WAU/cohort retention.
 *
 * Every widget is wrapped in a <TabErrorBoundary> so one render failure
 * doesn't take down the tab. Each hook owns its own loading / error
 * handling — error states render an inline retry card matching the
 * convention from OverviewPage / EngagementPage / QualityPage.
 *
 * Cost-trend chart: single left Y-axis in USD (bars) plus a muted overlay
 * line for request count on a second Y-axis — per plan's risk note, we
 * default to cost as the primary axis. Requests line is opacity-muted so
 * the eye reads cost first.
 */

import { RecountNotice } from '@/components/RecountNotice';
import * as React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  useComplexityDistribution,
  useCostTrend,
  useErrorBreakdown,
  useLatencyDistribution,
  useOperationsKpis,
  useToolUsage,
} from '@/hooks/use-operations';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { readTableSort, resolveRange, type TableSort } from '@/lib/filters-schema';
import { effectiveGranularity } from '@/lib/granularity';
import { formatBucketLabel } from '@/lib/format-bucket';
import { formatRelativeCet } from '@/lib/format-date';
import { errorClassLabel } from '@/lib/error-class-labels';
import {
  formatCompactNumber,
  formatCurrency,
  formatLatencyMs,
  formatPercent,
} from '@/lib/format';

import { chartPalette } from '@/lib/chart-palette';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ChartTooltip, useChartTooltipTrigger } from '@/components/charts/ChartTooltip';
import {
  TimelineTotalsBadge,
  sumField,
  type TimelineTotalItem,
} from '@/components/charts/TimelineTotalsBadge';
import { Donut, type DonutSegment } from '@/components/Donut';
import { ErrorBucketModal } from '@/components/ErrorBucketModal';
import { KPICard } from '@/components/KPICard';
import {
  LatencyDistribution,
  type LatencyDistributionData,
} from '@/components/LatencyDistribution';
import { InteractiveChartHint } from '@/components/charts/InteractiveChartHint';
import { SortableTable, type SortableColumn } from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { TraceDrillDownModal } from '@/components/TraceDrillDownModal';
import type {
  ComplexityRow,
  CostTrendPoint,
  ErrorBreakdownRow,
  ToolUsageRow,
  TraceComplexity,
  TraceLatencyBand,
} from '@/services/api-types';

// ---------------------------------------------------------------------------
// Inline retry card — mirrors OverviewPage / EngagementPage / QualityPage.
// ---------------------------------------------------------------------------

function ErrorRetry({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card data-testid={`${label}-error`}>
      <CardContent className="p-4 space-y-2">
        <div className="text-sm font-medium text-red-600">
          {label} failed to load
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs underline text-primary hover:no-underline"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Cost trend chart — IA §5.1.2
// ---------------------------------------------------------------------------

// Operations P1-2 — the local formatter previously dropped hour granularity,
// so every hourly bucket in a 24h preset rendered as "Apr 22" / "Apr 23" and
// Recharts deduped the ticks to two labels. Use the shared granularity-aware
// helper (matches EngagementPage / BriefsPage / QualityPage) so hourly
// buckets render as "Apr 22 14:00" and daily buckets stay "Apr 22".
function CostTrendChart({
  data,
  loading,
  granularity,
  height = 280,
}: {
  data: CostTrendPoint[];
  loading: boolean;
  granularity: 'hourly' | 'daily' | 'weekly';
  height?: number;
}): React.ReactElement {
  // Mobile tap persistence — see ChartTooltip.useChartTooltipTrigger.
  const tooltipTrigger = useChartTooltipTrigger();
  // Mobile audit 2026-04-24: `trigger="click"` keeps the cost tooltip open
  // until the user taps another bar, but tapping ANYWHERE else on the page
  // (including scrolling away) leaves the tooltip stuck on top of other
  // widgets. Wire a document-level pointerdown listener that bumps a
  // remount key when the user taps outside the chart, which force-unmounts
  // Recharts' internal tooltip state. Only active on touch so the desktop
  // hover path is unaffected.
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [dismissKey, setDismissKey] = React.useState(0);
  React.useEffect(() => {
    if (tooltipTrigger.trigger !== 'click') return;
    const onPointerDown = (e: PointerEvent) => {
      const root = containerRef.current;
      if (!root) return;
      if (e.target instanceof Node && !root.contains(e.target)) {
        setDismissKey((k) => k + 1);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [tooltipTrigger.trigger]);
  if (loading) {
    return (
      <div
        data-testid="cost-trend-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />
    );
  }
  if (!data || data.length === 0) {
    return (
      <div
        data-testid="cost-trend-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No cost data in selected period
      </div>
    );
  }
  return (
    <div
      ref={containerRef}
      data-testid="cost-trend"
      style={{ width: '100%', height }}
    >
      <ResponsiveContainer key={dismissKey} width="100%" height={height}>
        <ComposedChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(v: string) => formatBucketLabel(v, granularity)}
          />
          <YAxis
            yAxisId="cost"
            tickFormatter={(v: number) => formatCurrency(v)}
          />
          <YAxis
            yAxisId="requests"
            orientation="right"
            tickFormatter={(v: number) => formatCompactNumber(v)}
          />
          {/* bug F1 — themed tooltip. Mobile-tap persistence via trigger switch. */}
          <Tooltip
            {...tooltipTrigger}
            cursor={false}
            content={
              <ChartTooltip
                labelFormatter={(v) =>
                  typeof v === 'string'
                    ? formatBucketLabel(v, granularity)
                    : String(v ?? '')
                }
                valueFormatter={(value, name) => {
                  if (name === 'Cost')
                    return `Cost: ${formatCurrency(Number(value ?? 0))}`;
                  return `Requests: ${formatCompactNumber(Number(value ?? 0))}`;
                }}
              />
            }
          />
          <Legend />
          <Bar
            yAxisId="cost"
            dataKey="costUsd"
            fill={chartPalette.primary}
            name="Cost"
            isAnimationActive={false}
          />
          {/* P2-4 — the Requests overlay was getting lost on narrow mobile
              plots (muted slate at opacity 0.7, strokeWidth 2). Thicker
              stroke + visible dots + higher opacity keep it legible while
              staying visually secondary to the cost bars. */}
          <Line
            yAxisId="requests"
            type="monotone"
            dataKey="requests"
            stroke={chartPalette.muted}
            strokeWidth={2.5}
            name="Requests"
            dot={{ r: 2.5, fill: chartPalette.muted, strokeWidth: 0 }}
            activeDot={{ r: 4 }}
            opacity={0.9}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Complexity donut helpers — IA §5.1.7
// ---------------------------------------------------------------------------

/**
 * Colour per complexity tier — trivial is green, expert is red.
 *
 * Reactive traces whose DST classifier never tagged a complexity are now
 * dropped at the query level (`computeComplexityDistribution` adds
 * `isNotNull(langfuseTraces.complexity)` to the WHERE clause), so there is
 * no longer an `'unknown'` bucket here. If the backend ever starts emitting
 * new tier keys, `complexityDisplayLabel` + the defensive append in
 * `complexitySegments` still render them — just in a neutral grey.
 */
const COMPLEXITY_COLORS: Record<string, string> = {
  trivial: chartPalette.complexity.trivial,
  simple: chartPalette.complexity.simple,
  medium: chartPalette.complexity.medium,
  complex: chartPalette.complexity.complex,
  expert: chartPalette.complexity.expert,
};

/**
 * Human-readable label per complexity key. Anything unexpected falls back to
 * a Title-Case rendering of the raw key so new backend labels never ship as
 * `undefined` to the chart legend.
 */
const COMPLEXITY_LABELS: Record<string, string> = {
  trivial: 'Trivial',
  simple: 'Simple',
  medium: 'Medium',
  complex: 'Complex',
  expert: 'Expert',
};

function complexityDisplayLabel(key: string): string {
  return (
    COMPLEXITY_LABELS[key] ??
    key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
  );
}

/**
 * Canonical tier order, lowest → highest effort. Used both to drive the
 * donut legend (P3-3 — previously the legend came back in count-desc order
 * so easy and expert tiers were interleaved) and to keep the palette flowing
 * green → red top-to-bottom.
 */
const COMPLEXITY_TIER_ORDER: readonly string[] = [
  'trivial',
  'simple',
  'medium',
  'complex',
  'expert',
] as const;

function complexitySegments(rows: ComplexityRow[]): DonutSegment[] {
  // P3-3 — order segments by the tier palette (easy → expert), not by count.
  // The legend below the donut inherits this order so readers see the green
  // → red gradient match the severity axis.
  const byKey = new Map<string, ComplexityRow>(
    rows.map((r) => [r.complexity as unknown as string, r] as const),
  );
  const ordered: ComplexityRow[] = [];
  for (const tier of COMPLEXITY_TIER_ORDER) {
    const row = byKey.get(tier);
    if (row) ordered.push(row);
  }
  // Append anything the backend sends that we don't know about so a newly
  // introduced tier still renders (matches the pre-existing defensive path
  // in `complexityDisplayLabel`).
  for (const row of rows) {
    const key = row.complexity as unknown as string;
    if (!COMPLEXITY_TIER_ORDER.includes(key)) ordered.push(row);
  }
  return ordered
    .filter((r) => r.count > 0)
    .map((r) => ({
      label: complexityDisplayLabel(r.complexity as string),
      value: r.count,
      // Cast through unknown because TS narrows `ComplexityLabel` to the
      // legacy six keys; the defensive handling above may see new strings
      // from Agent A's backend fix before api-types is updated.
      color:
        COMPLEXITY_COLORS[r.complexity as unknown as string] ?? chartPalette.muted,
    }));
}

/**
 * Reverse of `complexityDisplayLabel` — map the Donut's click-emitted label
 * ("Trivial"/"Simple"/...) back to the backend enum. Falls back to a lowercase
 * round-trip so a hypothetical future tier still resolves.
 */
function complexityKeyFromLabel(label: string): TraceComplexity | null {
  for (const key of COMPLEXITY_TIER_ORDER) {
    if (complexityDisplayLabel(key) === label) return key as TraceComplexity;
  }
  return null;
}

/**
 * Complexity panel — donut only. The donut's own legend names each tier
 * and the green→red palette makes the severity axis self-evident; an
 * external caption repeating the same labels was adding visual noise and
 * duplicated the legend the donut already draws.
 */
function ComplexityPanel({
  rows,
  onSegmentClick,
  selectedLabel,
  loading,
}: {
  rows: ComplexityRow[];
  onSegmentClick?: (segment: DonutSegment | null) => void;
  selectedLabel?: string | null;
  loading?: boolean;
}): React.ReactElement {
  const total = rows.reduce((s, r) => s + r.count, 0);
  const segments = complexitySegments(rows);
  return (
    <div data-testid="complexity-distribution" className="space-y-3">
      <Donut
        data={segments}
        /* The centre label counts `rows`, so it reads "0 traces" over an
           uncounted window unless the chart itself is blanked. */
        loading={loading}
        /* simple / medium / complex — a fixed set. */
        legendRows={3}
        centerLabel={total > 0 ? `${total} traces` : undefined}
        onSegmentClick={onSegmentClick}
        selectedLabel={selectedLabel ?? null}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table column definitions
// ---------------------------------------------------------------------------

/** Short relative-time string for `lastSeen`. */
function formatRelative(iso: string): string {
  return formatRelativeCet(iso);
}

const ERRORS_TABLE_ID = 'errors';
const TOOLS_TABLE_ID = 'tools';

/**
 * Bug J3 — client-side sort for Operations tables.
 *
 * The Operations endpoints don't support server-side sort (unlike
 * Engagement's /top-users and Quality's /by-user which do), and the rows
 * are already capped at 25 per table, so client-side sort is both simpler
 * and appropriate. `SortableTable` writes `sort[<tableId>]` to the URL;
 * we read it here and apply a stable ordering before handing the rows to
 * the table.
 *
 * A default sort (descending by the specified count-like column) is
 * applied when the URL has no entry, so the tables always present "top N
 * by count" on first render — matching the server's existing ORDER BY.
 */
function sortRows<T extends Record<string, unknown>>(
  rows: T[],
  sort: TableSort | undefined,
  defaultSort: TableSort,
): T[] {
  const effective = sort ?? defaultSort;
  const key = effective.column as keyof T;
  const dir = effective.dir === 'asc' ? 1 : -1;
  // `slice()` — never mutate the upstream query cache array.
  return rows.slice().sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    // null/undefined always sink to the bottom regardless of direction —
    // otherwise toggling asc/desc on "Avg cost" with nulls gives confusing
    // "empty rows on top" UX.
    const aMissing = av === null || av === undefined;
    const bMissing = bv === null || bv === undefined;
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    // Dates come over the wire as ISO strings; lexicographic compare is
    // chronologically correct for ISO-8601 so no Date() parse needed.
    return String(av).localeCompare(String(bv)) * dir;
  });
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function OperationsPage(): React.ReactElement {
  const { filters } = useUrlFilters();

  // Error-breakdown click-to-modal drill-down. `bucket === null` means the
  // dialog is closed; setting a non-null value opens it and triggers
  // `useErrorBucketTraces` on the network tab.
  const [errorBucket, setErrorBucket] = React.useState<string | null>(null);
  const handleErrorRowClick = React.useCallback(
    (row: ErrorBreakdownRow) => setErrorBucket(row.errorClass),
    [],
  );
  const handleCloseErrorModal = React.useCallback(
    () => setErrorBucket(null),
    [],
  );

  // v1.4 dashboard-ux-fix: latency-bar + complexity-donut drill-downs. Each
  // keeps a single nullable "selected band/tier" piece of state; `null`
  // closes the modal AND keeps the shared hook inert (enabled: false).
  const [drillDownLatency, setDrillDownLatency] =
    React.useState<TraceLatencyBand | null>(null);
  const [drillDownComplexity, setDrillDownComplexity] =
    React.useState<TraceComplexity | null>(null);
  const handleComplexitySegmentClick = React.useCallback(
    (segment: DonutSegment | null) => {
      if (!segment) {
        setDrillDownComplexity(null);
        return;
      }
      const key = complexityKeyFromLabel(segment.label);
      if (key) setDrillDownComplexity(key);
    },
    [],
  );
  const selectedComplexityLabel = React.useMemo(
    () =>
      drillDownComplexity
        ? complexityDisplayLabel(drillDownComplexity)
        : null,
    [drillDownComplexity],
  );

  // Effective granularity — parity with Overview / Engagement / Quality.
  // Server's auto-rule still pins the real bucket size on responses.
  // Used by CostTrendChart so hourly presets render hour-aware tick labels
  // ("Apr 22 14:00") instead of collapsing to duplicate "Apr 22" ticks
  // (Operations P1-2). Matches the shared `formatBucketLabel` semantics
  // used by Engagement / Quality / Briefs.
  const range = resolveRange(filters);
  const eff = effectiveGranularity(filters.granularity, range.from, range.to);

  const kpis = useOperationsKpis(filters);
  const costTrend = useCostTrend(filters, filters.granularity);
  const latency = useLatencyDistribution(filters);
  const errors = useErrorBreakdown(filters, { limit: 20 });
  const tools = useToolUsage(filters, { limit: 25 });
  const complexity = useComplexityDistribution(filters);

  // bug J3 — client-side sort for the three tables on this page. The URL
  // stores `sort[<tableId>]` and `SortableTable` already writes to it on
  // header clicks; we read it here and re-sort the rows before passing them
  // in. Defaults mirror the server ORDER BY so the tables load the same way
  // they did before the fix.
  const errorsSort = readTableSort(filters, ERRORS_TABLE_ID);
  const toolsSort = readTableSort(filters, TOOLS_TABLE_ID);
  const DEFAULT_COUNT_DESC: TableSort = { column: 'count', dir: 'desc' };
  const DEFAULT_CALLS_DESC: TableSort = { column: 'calls', dir: 'desc' };

  const sortedErrorRows = React.useMemo(
    () =>
      sortRows(
        (errors.data ?? []) as unknown as Array<Record<string, unknown>>,
        errorsSort,
        DEFAULT_COUNT_DESC,
      ) as unknown as ErrorBreakdownRow[],
    [errors.data, errorsSort],
  );
  const sortedToolRows = React.useMemo(
    () =>
      sortRows(
        (tools.data ?? []) as unknown as Array<Record<string, unknown>>,
        toolsSort,
        DEFAULT_CALLS_DESC,
      ) as unknown as ToolUsageRow[],
    [tools.data, toolsSort],
  );

  const k = kpis.data;

  // Error rate + refusal rate are 0..1 from the backend; render as % inside
  // the KPI cards with the `percent` format achieved via a pre-formatted
  // string (KPICard supports pass-through strings).
  const errorPct =
    k === undefined ? null : Number((k.errorRate.current * 100).toFixed(2));
  const refusalPct =
    k === undefined
      ? null
      : Number((k.safetyRefusalRate.current * 100).toFixed(2));

  // --- Table column specs -----------------------------------------------

  const errorColumns: Array<SortableColumn<ErrorBreakdownRow>> =
    React.useMemo(
      () => [
        {
          key: 'errorClass',
          header: 'Error class',
          sortable: true,
          // Display-only label transform — sort key stays the raw bucket ID
          // (so `malformed_function_call` groups with itself, not jumbled
          // with `Malformed tool call`'s alphabetical neighbours), and the
          // drill-down still keys off `row.errorClass` (`handleErrorRowClick`
          // below). Mapping lives in `lib/error-class-labels.ts`.
          format: (v) => errorClassLabel(v as string),
        },
        {
          key: 'count',
          header: 'Count',
          sortable: true,
          align: 'right',
          format: (v) => String(v ?? 0),
        },
        {
          key: 'sampleMessage',
          header: 'Sample message',
          sortable: true,
          format: (v) => {
            const s = (v ?? '') as string;
            if (!s) return '—';
            // Truncate long messages. P3-4 — when truncated, expose the
            // full message via the native `title` attribute so hover /
            // long-press reveals it. Using the native tooltip keeps the
            // affordance lightweight (no Radix portal per row) and works
            // without JS on keyboard focus via the `tabIndex` below.
            if (s.length > 80) {
              return (
                <span title={s} tabIndex={0} className="outline-none">
                  {`${s.slice(0, 77)}…`}
                </span>
              );
            }
            return s;
          },
        },
        {
          key: 'lastSeen',
          header: 'Last seen',
          sortable: true,
          format: (v) => formatRelative(String(v)),
        },
      ],
      [],
    );

  const toolColumns: Array<SortableColumn<ToolUsageRow>> =
    React.useMemo(
      () => [
        { key: 'tool', header: 'Tool', sortable: true },
        {
          key: 'calls',
          header: 'Calls',
          sortable: true,
          align: 'right',
        },
        {
          key: 'avgLatencyMs',
          header: 'Avg latency',
          sortable: true,
          align: 'right',
          format: (v) => formatLatencyMs(v as number | null),
        },
        {
          key: 'avgCostUsd',
          header: 'Avg cost',
          sortable: true,
          align: 'right',
          format: (v) => formatCurrency(v as number | null),
        },
        {
          key: 'successRate',
          header: 'Success',
          sortable: true,
          align: 'right',
          format: (v) =>
            v === null || v === undefined
              ? '—'
              : formatPercent((v as number) * 100),
        },
      ],
      [],
    );

  return (
    <section aria-labelledby="operations-heading" className="space-y-6">
      <header>
        <h1 id="operations-heading" className="text-2xl font-semibold">
          Operations
        </h1>
        <p className="text-sm text-muted-foreground">
          Is it running fast, cheap, and safe — cost, latency, errors, and tool
          mix.
        </p>
        <RecountNotice queries={[kpis, costTrend, latency, errors, tools, complexity]} />
      </header>

      {/* Row 1 — KPI grid (IA §5.1.1). 4 cards on desktop. */}
      <TabErrorBoundary name="KPI row">
        {kpis.isError ? (
          <ErrorRetry label="KPIs" onRetry={() => void kpis.refetch()} />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
            <KPICard
              label="Total cost"
              value={k?.totalCost.current ?? null}
              format="currency"
              delta={k?.totalCost.delta ?? null}
              /* Positive delta = more spend → bad. */
              inverted
              loading={kpis.isLoading}
              tooltip="Sum of LLM cost (USD) across all traces in the window. Pulled from Langfuse's per-trace usage cost."
              // P2-3 — forward raw current/previous so the delta pill's
              // tooltip surfaces the absolute scale even when the ratio is
              // dramatic (e.g. first vs empty period).
              deltaCurrent={k?.totalCost.current ?? null}
              deltaPrevious={k?.totalCost.previous ?? null}
            />
            <KPICard
              label="Avg latency"
              value={
                k === undefined
                  ? null
                  : k.avgLatencyMs.current === null
                    ? null
                    : formatLatencyMs(k.avgLatencyMs.current)
              }
              delta={k?.avgLatencyMs.delta ?? null}
              inverted
              loading={kpis.isLoading}
              tooltip="Mean end-to-end latency per trace — time from Slack message to bot reply. Auto-scaled to ms or s; values ≥1000 ms are shown in seconds."
              deltaCurrent={k?.avgLatencyMs.current ?? null}
              deltaPrevious={k?.avgLatencyMs.previous ?? null}
            />
            <KPICard
              label="Error rate"
              value={errorPct}
              format="percent"
              delta={k?.errorRate.delta ?? null}
              subLabel={errorPct === null ? undefined : 'of traces'}
              inverted
              loading={kpis.isLoading}
              tooltip="Share of traces that raised a real failure — `raw_metadata.error` is a non-empty string (exceptions, LLM/tool errors, timeouts) or `level='ERROR'`. Safety refusals are tracked separately in the next card."
              // P2-3 — scale 0..1 ratios to 0..100 so the delta tooltip
              // reads in the same units as the rendered value ("0.24%").
              deltaCurrent={
                k?.errorRate.current === undefined ||
                k?.errorRate.current === null
                  ? null
                  : k.errorRate.current * 100
              }
              deltaPrevious={
                k?.errorRate.previous === undefined ||
                k?.errorRate.previous === null
                  ? null
                  : k.errorRate.previous * 100
              }
            />
            <KPICard
              label="Refusal rate"
              value={refusalPct}
              format="percent"
              delta={k?.safetyRefusalRate.delta ?? null}
              subLabel={refusalPct === null ? undefined : 'safety refusals'}
              inverted
              loading={kpis.isLoading}
              tooltip="% of traces where the pipeline flagged a safety refusal (metadata.safetyRefused). Covers LLMSafetyError and any other pipeline path that sets the flag — high numbers hint at prompt-tuning needs."
              deltaCurrent={
                k?.safetyRefusalRate.current === undefined ||
                k?.safetyRefusalRate.current === null
                  ? null
                  : k.safetyRefusalRate.current * 100
              }
              deltaPrevious={
                k?.safetyRefusalRate.previous === undefined ||
                k?.safetyRefusalRate.previous === null
                  ? null
                  : k.safetyRefusalRate.previous * 100
              }
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* Row 2 — Cost trend (2/3) + Latency distribution (1/3). IA §5.1.2 / §5.1.3. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <TabErrorBoundary name="Cost trend">
            {costTrend.isError ? (
              <ErrorRetry
                label="Cost trend"
                onRetry={() => void costTrend.refetch()}
              />
            ) : (
              <Card>
                <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Cost trend (USD + requests)
                  </CardTitle>
                  <TimelineTotalsBadge
                    data-testid="cost-trend-totals"
                    loading={costTrend.isLoading}
                    items={[
                      {
                        key: 'costUsd',
                        label: 'Cost',
                        color: chartPalette.primary,
                        format: 'currency',
                        value: sumField(costTrend.data ?? [], 'costUsd'),
                      },
                      {
                        key: 'requests',
                        label: 'Requests',
                        color: chartPalette.muted,
                        value: sumField(costTrend.data ?? [], 'requests'),
                      },
                    ] satisfies TimelineTotalItem[]}
                  />
                </CardHeader>
                <CardContent className="p-4">
                  <CostTrendChart
                    data={costTrend.data ?? []}
                    loading={costTrend.isLoading}
                    granularity={eff}
                  />
                </CardContent>
              </Card>
            )}
          </TabErrorBoundary>
        </div>
        <TabErrorBoundary name="Latency distribution">
          {latency.isError ? (
            <ErrorRetry
              label="Latency distribution"
              onRetry={() => void latency.refetch()}
            />
          ) : (
            <Card>
              {/* Mobile audit 2026-04-24: `flex-wrap` + `gap-y-2` so the
                  "Tap to drilldown" chip wraps below the title on iPhone
                  SE (375px) instead of either clipping or squashing
                  `Latency distribution` onto a single cramped line. */}
              <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Latency distribution
                </CardTitle>
                {/* Mobile audit 2026-04-24: only promise a drilldown when
                    the chart actually has data. The hint previously
                    rendered during skeletons and on the empty state,
                    promising an interaction that did nothing. */}
                {!latency.isLoading &&
                  (latency.data?.total ?? 0) > 0 && (
                    <InteractiveChartHint data-testid="latency-distribution-hint" />
                  )}
              </CardHeader>
              <CardContent className="p-4">
                <LatencyDistribution
                  data={
                    (latency.data ?? {
                      buckets: [],
                      p50: null,
                      p95: null,
                      p99: null,
                      total: 0,
                    }) as LatencyDistributionData
                  }
                  isLoading={latency.isLoading}
                  onBarClick={(band) => setDrillDownLatency(band)}
                />
              </CardContent>
            </Card>
          )}
        </TabErrorBoundary>
      </div>

      {/* Row 3 — Error breakdown (2/3) + Complexity donut (1/3).
          IA §5.1.4 / §5.1.7. */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <TabErrorBoundary name="Error breakdown">
            {errors.isError ? (
              <ErrorRetry
                label="Error breakdown"
                onRetry={() => void errors.refetch()}
              />
            ) : (
              <div className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">
                  Error breakdown (top 20)
                </div>
                <SortableTable<ErrorBreakdownRow>
                  tableId={ERRORS_TABLE_ID}
                  columns={errorColumns}
                  rows={sortedErrorRows}
                  defaultSort={DEFAULT_COUNT_DESC}
                  getRowKey={(r) => r.errorClass}
                  isLoading={errors.isLoading}
                  emptyMessage="No errors in selected period"
                  onRowClick={handleErrorRowClick}
                  // Operations P2-1 — stacked mobile layout so the
                  // `Sample message` + `Last seen` columns stop being
                  // clipped on narrow viewports.
                  mobileRender={(row) => {
                    const sample = (row.sampleMessage ?? '') as string;
                    const trimmed =
                      sample.length > 120 ? `${sample.slice(0, 117)}…` : sample;
                    return (
                      <div className="space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <div
                            className="font-medium text-foreground break-words"
                            title={row.errorClass}
                          >
                            {errorClassLabel(row.errorClass)}
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <span className="text-sm font-semibold tabular-nums">
                              {row.count}
                            </span>
                            {/* Mobile audit 2026-04-24: explicit chevron
                                so touch users can see the row opens a
                                drilldown — hover affordance is gated
                                behind `@media (hover: hover)` per
                                tailwind.config.js, leaving touch users
                                with no visual cue that the row is
                                clickable. */}
                            <ChevronRight
                              aria-hidden
                              className="h-4 w-4 text-muted-foreground"
                            />
                          </div>
                        </div>
                        {trimmed && (
                          <div
                            className="text-xs text-muted-foreground break-words"
                            title={sample.length > 120 ? sample : undefined}
                          >
                            {trimmed}
                          </div>
                        )}
                        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Last seen · {formatRelative(row.lastSeen)}
                        </div>
                      </div>
                    );
                  }}
                />
              </div>
            )}
          </TabErrorBoundary>
        </div>
        <TabErrorBoundary name="Complexity distribution">
          {complexity.isError ? (
            <ErrorRetry
              label="Complexity distribution"
              onRetry={() => void complexity.refetch()}
            />
          ) : (
            <Card>
              {/* Mobile audit 2026-04-24: match the Latency header wrap
                  behaviour so the drilldown chip can stack below the
                  title on iPhone SE. */}
              <CardHeader className="p-4 pb-0 flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 space-y-0">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Complexity distribution
                </CardTitle>
                {/* Mobile audit 2026-04-24: suppress the drilldown chip
                    on loading + empty states — only paint it when at
                    least one tier has traces to open. */}
                {!complexity.isLoading &&
                  (complexity.data ?? []).some((r) => r.count > 0) && (
                    <InteractiveChartHint data-testid="complexity-distribution-hint" />
                  )}
                {/* BUG-8: scheduled briefs are excluded upstream
                    (`computeComplexityDistribution` filters `scheduled=false`);
                    the `Unclassified` slice is reactive traces that DST did
                    not tag with a complexity. Donut + tier colours are
                    self-explanatory — no extra prose needed. */}
              </CardHeader>
              <CardContent className="p-4">
                {/* The hand-rolled 220px block that used to stand here was
                    a third of the height of the donut it replaced. The chart
                    owns its own loading state now — one idiom, one box. */}
                <ComplexityPanel
                  rows={complexity.data ?? []}
                  loading={complexity.isLoading}
                  onSegmentClick={handleComplexitySegmentClick}
                  selectedLabel={selectedComplexityLabel}
                />
              </CardContent>
            </Card>
          )}
        </TabErrorBoundary>
      </div>

      {/* Row 5 — Tool usage table (collapsible, default closed).
          IA §5.1.5. Skills moved to the dedicated /skills tab in
          Phase 9. Wrapped in a Collapsible because the top-25 tools
          table is long and secondary to the KPIs / funnel above —
          operators expand when they want to drill in. */}
      <Collapsible defaultOpen={false} className="space-y-2">
        <CollapsibleTrigger
          data-testid="tools-skills-toggle"
          className="group flex w-full items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted/60"
        >
          <span>Tool usage (top 25)</span>
          <ChevronDown
            aria-hidden
            className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180"
          />
        </CollapsibleTrigger>
        {/* Mobile audit 2026-04-24: `forceMount` was keeping the table
            in the DOM while closed — on iPhone SE that's a 25-row
            sortable table's worth of layout work for content the user
            can't see. Sort state lives in the URL so unmounting is
            safe; Radix still animates the collapse on re-open. */}
        <CollapsibleContent>
          <div className="pt-2">
            <TabErrorBoundary name="Tool usage">
              {tools.isError ? (
                <ErrorRetry
                  label="Tool usage"
                  onRetry={() => void tools.refetch()}
                />
              ) : (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">
                    Tool usage (top 25)
                  </div>
                  <SortableTable<ToolUsageRow>
                    tableId={TOOLS_TABLE_ID}
                    columns={toolColumns}
                    rows={sortedToolRows}
                    defaultSort={DEFAULT_CALLS_DESC}
                    getRowKey={(r) => r.tool}
                    isLoading={tools.isLoading}
                    emptyMessage="No tool calls in selected period"
                    // Operations P2-1 — stacked mobile card so the Avg
                    // latency / Avg cost / Success columns don't clip.
                    mobileRender={(row) => (
                      <div className="space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium text-foreground break-words">
                            {row.tool}
                          </div>
                          <span className="shrink-0 text-sm font-semibold tabular-nums">
                            {row.calls}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground tabular-nums">
                          avg {formatLatencyMs(row.avgLatencyMs)} ·{' '}
                          {formatCurrency(row.avgCostUsd)}
                          {row.successRate !== null &&
                            row.successRate !== undefined && (
                              <> · success {formatPercent(row.successRate * 100)}</>
                            )}
                        </div>
                      </div>
                    )}
                  />
                </div>
              )}
            </TabErrorBoundary>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Error-breakdown click-to-modal drill-down. Rendered once per page;
          `bucket === null` keeps Radix's Dialog closed and the
          `useErrorBucketTraces` hook inert. */}
      <ErrorBucketModal
        bucket={errorBucket}
        filters={filters}
        onClose={handleCloseErrorModal}
      />

      {/* v1.4 dashboard-ux-fix: latency-bar drill-down. Inert when band null. */}
      <TraceDrillDownModal
        open={drillDownLatency !== null}
        onClose={() => setDrillDownLatency(null)}
        filters={filters}
        extras={
          drillDownLatency !== null
            ? { latencyBand: drillDownLatency }
            : {}
        }
        title={
          drillDownLatency !== null
            ? `Traces in ${drillDownLatency} latency band`
            : ''
        }
        subtitle="Every trace whose end-to-end latency falls in this bucket for the current filters."
        testIdPrefix="operations-latency-drilldown"
      />

      {/* v1.4 dashboard-ux-fix: complexity-donut drill-down. Inert when null. */}
      <TraceDrillDownModal
        open={drillDownComplexity !== null}
        onClose={() => setDrillDownComplexity(null)}
        filters={filters}
        extras={
          drillDownComplexity !== null
            ? { complexity: drillDownComplexity }
            : {}
        }
        title={
          drillDownComplexity !== null
            ? `Traces tagged ${complexityDisplayLabel(drillDownComplexity)}`
            : ''
        }
        subtitle="Every trace at this complexity tier for the current filters."
        testIdPrefix="operations-complexity-drilldown"
      />
    </section>
  );
}
